import {
  ArrowSquareOut,
  BehanceLogo,
  CodepenLogo,
  DiscordLogo,
  FacebookLogo,
  GithubLogo,
  InstagramLogo,
  LinkedinLogo,
  RedditLogo,
  SoundcloudLogo,
  TiktokLogo,
  TwitchLogo,
  TwitterLogo,
  WechatLogo,
  YoutubeLogo,
} from 'phosphor-icons';
import { c } from '../../../utils/tailwind-merge';
import { Link } from '../../atoms/Link/Link';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '../../molecules/Table/Table';
import {
  SocialMediaTableProps,
  SocialMediaType,
} from './SocialMediaTable.types';

const socialMediaLabels: Record<SocialMediaType, string> = {
  [SocialMediaType.YouTube]: 'YouTube',
  [SocialMediaType.Instagram]: 'Instagram',
  [SocialMediaType.Soundcloud]: 'Soundcloud',
  [SocialMediaType.Behance]: 'Behance',
  [SocialMediaType.Codepen]: 'Codepen',
  [SocialMediaType.Twitter]: 'Twitter',
  [SocialMediaType.Facebook]: 'Facebook',
  [SocialMediaType.LinkedIn]: 'LinkedIn',
  [SocialMediaType.TikTok]: 'TikTok',
  [SocialMediaType.Twitch]: 'Twitch',
  [SocialMediaType.Discord]: 'Discord',
  [SocialMediaType.GitHub]: 'GitHub',
  [SocialMediaType.Reddit]: 'Reddit',
  [SocialMediaType.WeChat]: 'WeChat',
};

const socialMediaIcons: Record<SocialMediaType, React.ComponentType<any>> = {
  [SocialMediaType.YouTube]: YoutubeLogo,
  [SocialMediaType.Instagram]: InstagramLogo,
  [SocialMediaType.Soundcloud]: SoundcloudLogo,
  [SocialMediaType.Behance]: BehanceLogo,
  [SocialMediaType.Codepen]: CodepenLogo,
  [SocialMediaType.Twitter]: TwitterLogo,
  [SocialMediaType.Facebook]: FacebookLogo,
  [SocialMediaType.LinkedIn]: LinkedinLogo,
  [SocialMediaType.TikTok]: TiktokLogo,
  [SocialMediaType.Twitch]: TwitchLogo,
  [SocialMediaType.Discord]: DiscordLogo,
  [SocialMediaType.GitHub]: GithubLogo,
  [SocialMediaType.Reddit]: RedditLogo,
  [SocialMediaType.WeChat]: WechatLogo,
};

const displayOrder: SocialMediaType[] = [
  SocialMediaType.YouTube,
  SocialMediaType.Instagram,
  SocialMediaType.Soundcloud,
  SocialMediaType.Behance,
  SocialMediaType.Codepen,
  SocialMediaType.Discord,
  SocialMediaType.Facebook,
  SocialMediaType.GitHub,
  SocialMediaType.LinkedIn,
  SocialMediaType.Reddit,
  SocialMediaType.WeChat,
];

export const SocialMediaTable = ({
  socialMedias,
  microcopy,
  className = '',
}: SocialMediaTableProps) => {
  return (
    <Table className={c('w-full !table-fixed', className)}>
      <colgroup>
        <col className="sm:w-[280px] w-[170px] md:w-[190px]" />
        <col className="w-auto" />
      </colgroup>

      <TableBody
        className="
          [&>tr>td:first-child]:whitespace-normal
          [&>tr>td:first-child]:break-words
          [&>tr>td]:py-1.5
        "
        rightColTextAlign="right"
      >
        {[...socialMedias]
          .sort(
            (a, b) =>
              displayOrder.indexOf(a.type) - displayOrder.indexOf(b.type)
          )
          .map((socialMedia) => {
            const IconComponent = socialMediaIcons[socialMedia.type];
            const label = socialMediaLabels[socialMedia.type];

            return (
              <TableRow key={`${socialMedia.type}-${socialMedia.url}`}>
                {socialMedias.indexOf(socialMedia) === 2 ||
                socialMedias.length === 1 ? (
                  <TableCell className="pr-1 pt-1 text-sm font-normal text-grey-700">
                    {' '}
                    {microcopy.socialMedia}{' '}
                  </TableCell>
                ) : (
                  <TableCell>&nbsp;</TableCell>
                )}
                <TableCell>
                  <div className="flex items-center gap-3">
                    {IconComponent && (
                      <IconComponent size={16} className="text-grey-700" />
                    )}
                    <Link
                      href={socialMedia.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex text-blue transition-colors hover:text-blue-600"
                      aria-label={`Visit ${label} profile`}
                    >
                      <span className="font-normal">{label}</span>
                      <ArrowSquareOut size={16} />
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};
